import { memo, useEffect, useState } from 'react';

import { faMobileAlt, faPlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import { Spinner } from 'react-bootstrap';
import { styled } from 'styled-components';

import { useEvent } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, ButtonVariants, FilePromptButton, Icon, Window, WindowActionsBar } from 'core/ui';

import { ApiWebSocketProvider } from 'features/api/fragments/ApiWebSocketProvider';
import { useMutationFiles } from 'features/api/hooks/useMutationFiles';
import { useMutationStudyShares } from 'features/api/hooks/useMutationStudyShares';
import { useQueryFilesByExamId } from 'features/api/hooks/useQueryFilesByExamId';
import { useApiWebSocketConnection } from 'features/auth/hooks/useApiWebSocketConnection';
import { DocumentCategories } from 'features/exam/constants';
import { SupportedFiles, ViewFileWindow } from 'features/file';
import { ShareType } from 'features/patient/constants';
import { QRScanModal } from 'features/upload-exams/fragments';

import { FileService } from '../services';
import { FileTypeHelper } from '../services/FileTypeHelper';
import { UploadWindowProps } from '../types';
import { UploadWindowFileCard } from './UploadWindowFileCard';

const UploadWindowInner = memo<UploadWindowProps>(({ examId, onClose, onChange }) => {
  const connection = useApiWebSocketConnection();

  const [fileViewWindowId, setFileViewWindowId] = useState<number | null>(null);
  const [linkId, setLinkId] = useState<string | null>();
  const [isQrModalVisible, setIsQrModalVisible] = useState<boolean>(false);

  const { data: files, isLoading: isFilesLoading } = useQueryFilesByExamId(examId);
  const { invalidateFilesQuery, uploadFilesMutation } = useMutationFiles(examId);
  const { addStudyShareMutation } = useMutationStudyShares();

  const attachmentFiles =
    files == null
      ? []
      : files
          .filter((file) => file.categoryId !== DocumentCategories.EXAM.value && FileTypeHelper.getUppercaseFileType(file) !== 'LINK')
          .sort((a, b) => b.id - a.id);

  const handleUploadFilesSelected = useEvent(async (selectedFiles: File[]) => {
    const invalidFiles = FileService.validateFormFiles(selectedFiles);

    if (invalidFiles.length > 0) {
      NotificationsService.displayError(`${FileService.invalidFileMessage()}: ${invalidFiles.map((file) => file.name).join(', ')}`);
      return;
    }

    await uploadFilesMutation.mutateAsync({
      files: selectedFiles,
      shouldSplitFiles: false,
      examId,
      patientId: null,
      categoryId: DocumentCategories.DOCUMENT.value,
    });

    onChange?.();
  });

  const handleQrModalClose = useEvent(() => {
    setIsQrModalVisible(false);
    setLinkId(null);
  });

  const handleViewFileWindowClosed = useEvent(() => {
    setFileViewWindowId(null);
  });

  const handleQrUploadClick = useEvent(async () => {
    const linkId = crypto.randomUUID();
    const expireOn = dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss');

    await addStudyShareMutation.mutateAsync({
      id: 0,
      linkId: linkId,
      email: null,
      sharePassword: null,
      shareType: ShareType.Link,
      sharePatientId: null,
      studyShareExams: [{ exam_id: examId }],
      expireOn: expireOn,
      dateCreated: '',
      message: null,
      bypassSplashPage: true,
    });

    setLinkId(linkId);
    setIsQrModalVisible(true);
  });

  // Refresh the list of files when we receive an upload status update (IE: When a file is uploaded from a mobile device using a QR code).
  useEffect(() => {
    if (examId == null || examId === 0 || connection == null) return;

    connection.on('uploadStatus', invalidateFilesQuery);

    return () => {
      connection.off('uploadStatus', invalidateFilesQuery);
    };
  }, [connection, examId, invalidateFilesQuery]);

  return (
    <>
      <Window title={'Attachments'} onClose={onClose} initialHeight={500} initialWidth={450}>
        <StyledWindowContainer>
          <StyledFilesCardContainer>
            {isFilesLoading && (
              <StyledSpinnerContainer>
                <Spinner />
              </StyledSpinnerContainer>
            )}
            {!isFilesLoading && (
              <>
                {attachmentFiles.length > 0 && (
                  <div>
                    {attachmentFiles.map((file) => (
                      <UploadWindowFileCard key={file.id} file={file} disableActions={uploadFilesMutation.isPending} onViewFile={setFileViewWindowId} />
                    ))}
                  </div>
                )}
              </>
            )}
          </StyledFilesCardContainer>
        </StyledWindowContainer>

        <WindowActionsBar>
          <StyledActionsDiv>
            <StyledUploadContainer>
              <FilePromptButton
                onFilesSelected={handleUploadFilesSelected}
                variant={ButtonVariants.PRIMARY}
                disabled={uploadFilesMutation.isPending}
                title="Upload Files"
                accept={FileService.getAcceptedUploadFiles()}
                multiple
              >
                {uploadFilesMutation.isPending ? <Icon icon={faSpinner} spin={uploadFilesMutation.isPending}></Icon> : <Icon icon={faPlus}></Icon>}Upload
                File(s)
              </FilePromptButton>
              <SupportedFiles placement="bottom" />
            </StyledUploadContainer>

            <StyledMobileContainerDiv>
              <Button variant={ButtonVariants.PRIMARY} title="Mobile Upload" disabled={uploadFilesMutation.isPending} onClick={handleQrUploadClick}>
                <Icon icon={faMobileAlt} /> Mobile Upload
              </Button>
            </StyledMobileContainerDiv>
          </StyledActionsDiv>
        </WindowActionsBar>
      </Window>

      {!!fileViewWindowId && <ViewFileWindow title="View Attachment" fileId={fileViewWindowId} onClose={handleViewFileWindowClosed} />}
      {linkId && examId && <QRScanModal show={isQrModalVisible} onClose={handleQrModalClose} linkId={linkId} examId={examId} />}
    </>
  );
});

UploadWindowInner.displayName = 'UploadWindowInner';

export const UploadWindow = memo<UploadWindowProps>((props) => {
  return (
    <ApiWebSocketProvider>
      <UploadWindowInner {...props} />
    </ApiWebSocketProvider>
  );
});

UploadWindow.displayName = 'UploadWindow';

const StyledWindowContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledFilesCardContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[2]};
  overflow: auto;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const StyledActionsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledUploadContainer = styled.div`
  flex: 1 0 min-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledMobileContainerDiv = styled.div`
  flex: 1 0 min-content;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;
